import type { DataTransferObject } from '../../@types/DataTransferObject.js';
import type { IPayable } from '../../@types/Payable.js';
import type { BaseError } from '../../error/BaseError.js';
import { CollectionError } from '../../error/CollectionError.js';
import type { ClientErrorCode } from '../../error/constants.js';
import { PaymentError } from '../../error/PaymentError.js';
import { logger } from '../../utils/index.js';
import type { MiddlewareContextApi } from './createMiddlewareContextApi.js';

type PaidItem<TPayable extends IPayable> = Readonly<{
  payable: TPayable;
  dto: DataTransferObject;
}>;

type PaymentIterator<TPayable> = {
  readonly length: number;
  readonly count: number;
  readonly remaining: TPayable[];
  [Symbol.asyncIterator](): AsyncGenerator<TPayable, void, unknown>;
};

export type ApiPaymentContext<TPayable extends IPayable> = {
  mergeCollectionError: (collectionError: CollectionError) => boolean;
  addPaymentError: (error: BaseError<ClientErrorCode>) => void;
  getPaymentError: () => CollectionError | undefined;
  addPaidItem: (payable: TPayable, dto: DataTransferObject) => void;
  addPaidItems: (payableResponses: { payable: TPayable; dto: DataTransferObject }[]) => void;
  getPayableItems: () => TPayable[];
  getUnpaidPayableItems: () => readonly TPayable[];
  getPaidPayableItems: () => TPayable[];
  getPaidPayableItemsDTO: () => DataTransferObject[];
  getPaidPayableItemsData: () => readonly { payable: TPayable; dto: DataTransferObject }[];
  getPaidAmount: () => number;
  getUnpaidAmount: () => number;
  getTotalPrice: () => number;
  getAccountBalance: () => number;
  createPaymentsIterable: () => PaymentIterator<TPayable>;
};

/**
 * Creates a context object around the items that is requested to be paid.
 * The contest object is used internally for the payment process and contains convenience functions.
 *
 * Contains for example an iterable that can be used to iterate over the payment items that holds together tasks with the payment item.
 *
 * The payment context object is at the end passed to the `createPaymentResponseApi` when the payment request is settled and used to create the final payment response to the user.
 *
 * @param contextApi - The middleware context api, used to feed the context api with data
 * @returns Api object with functions to interact with the payment process
 */
export function createPaymentContextApi<TPayable extends IPayable>(
  contextApi: MiddlewareContextApi<TPayable>
): ApiPaymentContext<TPayable> {
  const hostContextPayableItems = contextApi.payment.getPayableItems();
  const hostPayableItems = [...hostContextPayableItems];
  let hostUnpaidPayableItems = [...hostContextPayableItems];
  const hostPaidPayableItems: PaidItem<TPayable>[] = [];
  let hostAccountBalance: number;
  const hostCollectionError = new CollectionError();

  // --- Error handling

  /**
   * Overrides (sets) existing payment error collection a new error collection.
   *
   * @param collectionError The error collection to set
   * @returns True if the error collection was set
   */
  const mergeCollectionError = (collectionError: CollectionError): boolean => {
    for (const error of collectionError.errors) {
      hostCollectionError.addError(error);
    }

    return true;
  };

  /**
   * Adds an error to the payment error collection.
   *
   * @param error The error to add
   */
  const addPaymentError = (error: BaseError<ClientErrorCode>): void => {
    hostCollectionError.addError(error);
  };

  /**
   * Returns the payment error collection.
   *
   * @returns The payment error collection
   */
  const getPaymentError = (): CollectionError | undefined => {
    const { errors } = hostCollectionError;
    if (errors.length === 0) {
      return undefined;
    }

    return hostCollectionError;
  };

  // --- Payment handling

  /**
   * Adds a payable item to the paid collection.
   *
   * @param payable The payable item to add
   * @param dto The payable response data transfer object
   */
  const addPaidItem = (payable: TPayable, dto: DataTransferObject): void => {
    hostUnpaidPayableItems = hostUnpaidPayableItems.filter((unpaidPayable) => unpaidPayable !== payable);

    hostAccountBalance = dto.balance ?? hostAccountBalance;

    hostPaidPayableItems.push({ payable, dto });
  };

  /**
   * Adds a collection of payable item to the paid collection.
   *
   * @param payableResponses Response from payment
   */
  const addPaidItems = (payableResponses: { payable: TPayable; dto: DataTransferObject }[]): void => {
    for (const { payable, dto } of payableResponses) {
      addPaidItem(payable, dto);
    }
  };

  /**
   * Gets all the items that is added to the payment process.
   *
   * @returns All the payable items
   */
  const getPayableItems = (): TPayable[] => hostPayableItems;

  /**
   * Gets all the items that is not yet paid or has failed at payment.
   *
   * @returns All the unpaid payable items
   */
  const getUnpaidPayableItems = (): readonly TPayable[] => Object.freeze(hostUnpaidPayableItems);

  /**
   * Gets all the items that has been paid.
   *
   * @returns All the paid payable items
   */
  const getPaidPayableItems = (): TPayable[] => hostPaidPayableItems.map(({ payable }) => payable);

  /**
   * Gets all the DTO for the payable items that has been paid.
   *
   * @returns All the paid payable items DTO
   */
  const getPaidPayableItemsDTO = (): DataTransferObject[] => hostPaidPayableItems.map(({ dto }) => dto);

  /**
   * Gets all the items that has been paid along with payment DTO.
   *
   * @returns All the paid payable items with payment DTO, keys: { payable, dto }
   */
  const getPaidPayableItemsData = (): readonly { payable: TPayable; dto: DataTransferObject }[] =>
    Object.freeze(hostPaidPayableItems);

  /**
   * Gets the total amount of all the paid items.
   *
   * @returns The total amount of all the paid items
   */
  const getPaidAmount = (): number =>
    hostPaidPayableItems.map(({ dto }) => dto.cost).reduce((sum, cost) => sum + cost, 0);

  /**
   * Gets the total amount of all the unpaid items.
   *
   * @returns The total amount of all the unpaid items
   */
  const getUnpaidAmount = (): number => hostUnpaidPayableItems.reduce((sum, payable) => sum + payable.price, 0);

  /**
   * Gets the total amount of all the items.
   *
   * @returns The total amount of all the items
   */
  const getTotalPrice = (): number => contextApi.payment.getTotalPrice();

  /**
   * Gets the account balance.
   *
   * @returns Balance of the account in payment process
   */
  const getAccountBalance = (): number => hostAccountBalance;

  /**
   * Creates an iterable that can be used to iterate over the payment items
   * Calls the associated task (if any) for each payable item, if the task fails the payable item is added to the failed collection and will be skipped in the iteration.
   *
   * @returns - An iterable object
   */
  function createPaymentsIterable(): PaymentIterator<TPayable> {
    const itemsToPay = [...hostUnpaidPayableItems];
    const totalLength = itemsToPay.length;
    let count = 0;
    const failedPayableItems: TPayable[] = [];

    // The iterable object
    return Object.freeze({
      get length() {
        return totalLength;
      },
      get count() {
        return count;
      },
      get remaining() {
        return [...failedPayableItems, ...itemsToPay];
      },
      async *[Symbol.asyncIterator]() {
        while (itemsToPay.length > 0) {
          const payable = itemsToPay.shift();
          if (!payable) {
            break;
          }

          count++;
          try {
            // Run any tasks added for the current wager
            // Safe to disable the eslint rule here, since this is a generator function
            // eslint-disable-next-line no-await-in-loop
            await contextApi.payment.tasks.run(payable.localId);
          } catch (error) {
            const errorMessage = error instanceof Error ? error.message : 'Unknown error';
            logger.warn('Payment failed on task', {
              current: count,
              total: totalLength,
              error: errorMessage,
              payableId: payable.localId,
            });
            if (error instanceof PaymentError) {
              addPaymentError(error);
            }
            failedPayableItems.push(payable);

            // Tasks are blocking if rejected, so continue to the next payable item
            continue;
          }

          yield payable;
        }
      },
    });
  }

  return {
    // Error handling
    mergeCollectionError,
    addPaymentError,
    getPaymentError,
    // Payment handling
    addPaidItem,
    addPaidItems,
    getPayableItems,
    getUnpaidPayableItems,
    getPaidPayableItems,
    getPaidPayableItemsDTO,
    getPaidPayableItemsData,
    getPaidAmount,
    getUnpaidAmount,
    getTotalPrice,
    getAccountBalance,
    createPaymentsIterable,
  } as const;
}

export type PayableGenerator<TPayable extends IPayable> = ReturnType<
  ReturnType<typeof createPaymentContextApi<TPayable>>['createPaymentsIterable']
>;

export type PaymentContextApi<TPayable extends IPayable> = ReturnType<typeof createPaymentContextApi<TPayable>>;
